import React, { useEffect, useMemo, useState } from 'react';
import { GradeHorario, Professor, gradeService } from '../services/api';

const DIAS_SEMANA: Record<number, string> = {
  1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta', 5: 'Sexta', 6: 'Sábado', 7: 'Domingo',
};

interface SubstituirProfessorModalProps {
  isOpen: boolean;
  onClose: () => void;
  gradeId: string;
  horarios: GradeHorario[];
  professores: Professor[];
  onSucesso: () => void;
}

interface DisciplinaResumo {
  id: string;
  nome: string;
  professor: { id: string; nome: string };
  professor2: { id: string; nome: string } | null;
}

const SubstituirProfessorModal: React.FC<SubstituirProfessorModalProps> = ({
  isOpen,
  onClose,
  gradeId,
  horarios,
  professores,
  onSucesso,
}) => {
  const [disciplinaId, setDisciplinaId] = useState('');
  const [professorAntigoId, setProfessorAntigoId] = useState('');
  const [professorNovoId, setProfessorNovoId] = useState('');
  // null = ainda não escolhido. Obrigatório escolher Sim ou Não antes de poder confirmar.
  const [atualizarDisciplina, setAtualizarDisciplina] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);

  const disciplinas: DisciplinaResumo[] = useMemo(() => {
    const mapa = new Map<string, DisciplinaResumo>();
    horarios.forEach((h) => {
      if (!mapa.has(h.disciplina.id)) {
        mapa.set(h.disciplina.id, {
          id: h.disciplina.id,
          nome: h.disciplina.nome,
          professor: h.disciplina.professor,
          professor2: h.disciplina.professor2 ?? null,
        });
      }
    });
    return Array.from(mapa.values());
  }, [horarios]);

  const disciplinaSelecionada = disciplinas.find((d) => d.id === disciplinaId) ?? null;

  // Sempre TODOS os horários da disciplina com esse professor — não dá pra escolher uma
  // parte. Assumir uma disciplina é assumir todas as aulas dela (regra da escola).
  const linhasDaDisciplina = useMemo(() => {
    if (!disciplinaId || !professorAntigoId) return [];
    return horarios.filter((h) => {
      if (h.disciplina.id !== disciplinaId) return false;
      return h.disciplina.professor.id === professorAntigoId || h.disciplina.professor2?.id === professorAntigoId;
    });
  }, [horarios, disciplinaId, professorAntigoId]);

  useEffect(() => {
    if (!isOpen) {
      setDisciplinaId('');
      setProfessorAntigoId('');
      setProfessorNovoId('');
      setAtualizarDisciplina(null);
      setError(null);
      setAvisos([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const professoresDisponiveis = professores.filter((p) => {
    const pid = String(p.id);
    if (pid === professorAntigoId) return false;
    if (disciplinaSelecionada?.professor.id === pid) return false;
    if (disciplinaSelecionada?.professor2?.id === pid) return false;
    return true;
  });

  const handleConfirmar = async () => {
    if (!disciplinaId || !professorAntigoId || !professorNovoId) {
      setError('Selecione a disciplina, o professor que está saindo e o novo professor.');
      return;
    }
    if (atualizarDisciplina === null) {
      setError('Escolha Sim ou Não para "Atualizar também a disciplina" antes de confirmar.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const resultado = await gradeService.substituirProfessor(gradeId, {
        disciplinaId,
        professorAntigoId,
        professorNovoId,
        atualizarDisciplina,
      });
      setAvisos(resultado.avisos);
      onSucesso();
      if (resultado.avisos.length === 0) {
        onClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao substituir o professor.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-people-arrows me-2 text-warning"></i>
              Substituir Professor nesta Grade
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving}></button>
          </div>

          <div className="modal-body">
            <p className="text-muted">
              Troca o professor de uma disciplina <strong>só nesta grade já gerada</strong>, sem precisar regenerar nada.
              A troca é sempre da disciplina inteira — quem assume, assume todas as aulas dela.
              Para dividir as aulas entre 2 professores novos, separe a disciplina em registros menores primeiro
              (em Turmas e Disciplinas) e substitua cada um aqui.
            </p>

            {error && <div className="alert alert-danger">{error}</div>}
            {avisos.length > 0 && (
              <div className="alert alert-warning">
                <strong>Atenção — confira antes de fechar:</strong>
                <ul className="mb-0 mt-2">
                  {avisos.map((aviso, i) => <li key={i}>{aviso}</li>)}
                </ul>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold">Disciplina</label>
              <select className="form-select" value={disciplinaId} onChange={(e) => { setDisciplinaId(e.target.value); setProfessorAntigoId(''); setProfessorNovoId(''); }}>
                <option value="">Selecione...</option>
                {disciplinas.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nome} — {d.professor.nome}{d.professor2 ? ` e ${d.professor2.nome}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {disciplinaSelecionada && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Professor que está saindo</label>
                <select className="form-select" value={professorAntigoId} onChange={(e) => { setProfessorAntigoId(e.target.value); setProfessorNovoId(''); }}>
                  <option value="">Selecione...</option>
                  <option value={disciplinaSelecionada.professor.id}>{disciplinaSelecionada.professor.nome}</option>
                  {disciplinaSelecionada.professor2 && (
                    <option value={disciplinaSelecionada.professor2.id}>{disciplinaSelecionada.professor2.nome}</option>
                  )}
                </select>
              </div>
            )}

            {linhasDaDisciplina.length > 0 && (
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Horários que serão afetados ({linhasDaDisciplina.length})
                </label>
                <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {linhasDaDisciplina.map((h) => (
                    <div key={h.id} className="py-1">
                      <i className="fas fa-clock text-muted me-2"></i>
                      {h.turma.nome} — {DIAS_SEMANA[h.horario.diaSemana] ?? `dia ${h.horario.diaSemana}`}, {h.horario.inicio}–{h.horario.fim}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {professorAntigoId && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Novo professor</label>
                <select
                  className="form-select"
                  value={professorNovoId}
                  onChange={(e) => { setProfessorNovoId(e.target.value); setAtualizarDisciplina(null); }}
                >
                  <option value="">Selecione...</option>
                  {professoresDisponiveis.map((p) => (
                    <option key={p.id} value={String(p.id)}>{p.nome}</option>
                  ))}
                </select>
              </div>
            )}

            {professorNovoId && (
              <div
                className="p-3 rounded mb-3"
                style={{
                  border: atualizarDisciplina === null ? '2px solid #dc3545' : '2px solid #198754',
                  backgroundColor: atualizarDisciplina === null ? '#fff5f5' : '#f0fff4',
                }}
              >
                <label className="form-label fw-bold mb-1" style={{ fontSize: '1.05rem' }}>
                  <i className="fas fa-exclamation-circle me-2 text-danger"></i>
                  Atualizar também a disciplina?
                </label>
                <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                  Se marcar <strong>Sim</strong>, a próxima vez que a grade for gerada pra essa turma, esse novo professor já entra automaticamente.
                  Se marcar <strong>Não</strong>, a troca vale só pra esta grade — a disciplina continua oficialmente com o professor antigo.
                  <strong> É obrigatório escolher uma das duas opções.</strong>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAtualizarDisciplina(true)}
                    className={`btn flex-fill ${atualizarDisciplina === true ? 'btn-success' : 'btn-outline-success'}`}
                    style={{ borderRadius: '8px', fontWeight: '600' }}
                  >
                    Sim
                  </button>
                  <button
                    type="button"
                    onClick={() => setAtualizarDisciplina(false)}
                    className={`btn flex-fill ${atualizarDisciplina === false ? 'btn-success' : 'btn-outline-success'}`}
                    style={{ borderRadius: '8px', fontWeight: '600' }}
                  >
                    Não
                  </button>
                </div>
                {atualizarDisciplina === null && (
                  <small className="text-danger d-block mt-2">
                    <i className="fas fa-arrow-up me-1"></i>
                    Escolha Sim ou Não pra liberar a confirmação.
                  </small>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              {avisos.length > 0 ? 'Fechar' : 'Cancelar'}
            </button>
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleConfirmar}
              disabled={saving || !disciplinaId || !professorAntigoId || !professorNovoId || atualizarDisciplina === null}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Substituindo...
                </>
              ) : (
                <>
                  <i className="fas fa-people-arrows me-2"></i>
                  Confirmar Substituição
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubstituirProfessorModal;
